import type { ReactNode } from "react";
import { useId } from "react";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./client-access-switch.i18n";

type Props = {
  title: string;
  explanation: string;
  /** What advisors do while the setting is on. */
  advisorsDo: string;
  checked: boolean;
  /** The switch was moved but not saved yet. */
  changed: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Extra content under the explanation, e.g. the teams link. */
  children?: ReactNode;
};

/** QL-380 — one client access setting: switch, explanation and the "For advisors" line. */
export function ClientAccessSwitch({
  title,
  explanation,
  advisorsDo,
  checked,
  changed,
  disabled,
  onCheckedChange,
  children,
}: Props) {
  const t = useT(T);
  const id = useId();
  const descriptionId = `${id}-description`;

  return (
    <div className="flex items-start gap-3 rounded-lg border p-4">
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-describedby={descriptionId}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={id} className="text-sm font-semibold">
            {title}
          </Label>
          {changed && <Badge variant="warning">{t.preview}</Badge>}
        </div>
        <div id={descriptionId} className="space-y-2 text-sm">
          {/* The badge sits outside the description; say the unsaved state here too. */}
          {changed && <span className="sr-only">{t.preview}.</span>}
          <p className="text-muted-foreground">{explanation}</p>
          <p>
            <span className="font-medium">{t.advisorsDo}: </span>
            <span className="text-muted-foreground">{advisorsDo}</span>
          </p>
        </div>
        {children}
      </div>
    </div>
  );
}
